import { Typography, Icon, ICONS_ENUM } from '@sanlam-glacier/app-common/components';
import styles from './topFunds.module.scss';
import { ScriptableContext } from 'chart.js';
import clsx from 'clsx';
import { LineChartConfig, TopFunds } from 'modules/dashboard/types';
import LineChart from 'modules/dashboard/utils/chart/line-chart/LineChart';
import { ChartType } from 'modules/dashboard/utils/chart/ChartWidget';

const FundCard = ({
  name,
  amount,
  percentageOfBook,
  percentageGrowth,
  shortName,
  data,
  labels,
  isPositiveGrowth,
  index
}: TopFunds) => {
  return (
    <div className={styles.card} data-testid={`topFundsCard_${index}`}>
      <div className={styles.cardContainer}>
        <div className={styles.heading}>
          <Typography
            data-testid={`topFundsInvestmentLongNameLabel_${index}`}
            variant={Typography.VARIANT.B4}
            weight={Typography.WEIGHT.MEDIUM}
            className={styles.textEllipses}
          >
            {name}
          </Typography>
          <Typography
            data-testid={`topFundsInvestmentShortNameLabel_${index}`}
            variant={Typography.VARIANT.B4}
            weight={Typography.WEIGHT.NORMAL}
            color={Typography.COLOR.TERTIARY}
            className={styles.shortName}
          >
            ({shortName})
          </Typography>
        </div>
        <div className={styles.fundDetails}>
          <div className={styles.information}>
            <Typography
              data-testid={`topFundsInvestmentAmountLabel_${index}`}
              variant={Typography.VARIANT.B4}
              weight={Typography.WEIGHT.NORMAL}
              color={Typography.COLOR.TERTIARY}
              className={styles.textEllipses}
            >
              R {amount}
            </Typography>
            <div
              data-testid={`topFundsBookValueContainer_${index}`}
              className={styles.bookPercentage}
            >
              <Typography
                data-testid={`topFundsBookPercentageLabel_${index}`}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.MEDIUM}
              >
                {percentageOfBook}%
              </Typography>
              <Typography
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.NORMAL}
                color={Typography.COLOR.TERTIARY}
              >
                of book
              </Typography>
            </div>
            <div
              data-testid={`topFundsPillContainer_${index}`}
              className={clsx([
                styles.pillContainer,
                isPositiveGrowth ? styles.positive : styles.negative
              ])}
            >
              <Icon
                data-testid={`topFundsPillIcon_${index}`}
                icon={
                  isPositiveGrowth
                    ? ICONS_ENUM.NAVIGATION_ARROW_UPWARD
                    : ICONS_ENUM.NAVIGATION_ARROW_DOWNWARD
                }
                className={styles.growthIcon}
              />
              <p
                data-testid={`topFundsGrowthPercentageLabel_${index}`}
                className={styles.growthText}
              >
                {percentageGrowth}%
              </p>
            </div>
          </div>
          <div data-testid={`topFundsChartContainer_${index}`} className={styles.chartContainer}>
            <div data-testid={`topFundsChart_${index}`} className={styles.chart}>
              <LineChart
                chartType={ChartType.line}
                data={[data]}
                labels={labels}
                lineChartConfig={
                  isPositiveGrowth ? positiveLineChartConfig : negativeLineChartConfig
                }
              />
            </div>
            <Typography
              variant={Typography.VARIANT.B4}
              weight={Typography.WEIGHT.NORMAL}
              className={styles.textEllipses}
            >
              <p
                data-testid={`topFundsYearLabel_${index}`}
                className={isPositiveGrowth ? styles.textPositive : styles.textNegative}
              >
                1 YEAR
              </p>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundCard;

const topFundsChartConfig: LineChartConfig = {
  borderWidth: 2,
  tension: 0.3,
  pointRadius: 0,
  fill: true
};

const positiveLineChartConfig: LineChartConfig = {
  ...topFundsChartConfig,
  borderColor: '#10BD00',
  backgroundColor: (context: ScriptableContext<'line'>) => {
    const ctx = context.chart.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, 52);
    gradient.addColorStop(0, '#ECFCEB');
    gradient.addColorStop(1, '#FFFFFF');
    return gradient;
  }
};

const negativeLineChartConfig: LineChartConfig = {
  ...topFundsChartConfig,
  borderColor: '#A31202',
  backgroundColor: (context: ScriptableContext<'line'>) => {
    const ctx = context.chart.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, 52);
    gradient.addColorStop(0, '#FFF1F0');
    gradient.addColorStop(1, '#FFFFFF');
    return gradient;
  }
};
